import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import {
  AVAILABLE_LEARNING_LANGUAGES,
  DEFAULT_LEARNING_LANGUAGE_CODE,
  getLearningLanguage,
  normalizeLearningLanguageCode,
} from '../constants/learningLanguages';
import {
  isSelectableUiLanguageCode,
  normalizeUiLanguageCode,
  shouldUiLanguageFollowLearningLanguage,
} from '../constants/uiLanguages';
import { db } from '../firebase';
import { getPathBoundUiLanguage, getUrlDefaultUiLanguage } from '../lib/uiRouteLanguage';
import LanguageService from '../services/LanguageService';
import i18n from '../i18n';
import { mergeLearningLanguages } from './languageLearningHelpers';

const LanguageLearningContext = createContext();

const LEARNING_LANGUAGE_STORAGE_KEY = 'aprendos_learning_language';
const LEARNING_LANGUAGES_STORAGE_KEY = 'aprendos_learning_languages';

function readGuestLearningLanguages() {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(LEARNING_LANGUAGES_STORAGE_KEY)
      || JSON.stringify([DEFAULT_LEARNING_LANGUAGE_CODE])
    );

    if (!Array.isArray(parsed)) {
      return [DEFAULT_LEARNING_LANGUAGE_CODE];
    }

    return mergeLearningLanguages(parsed);
  } catch {
    return [DEFAULT_LEARNING_LANGUAGE_CODE];
  }
}

function persistGuestLearningLanguages(languageCodes) {
  localStorage.setItem(LEARNING_LANGUAGES_STORAGE_KEY, JSON.stringify(languageCodes));
}

export const useLanguageLearning = () => {
  const context = useContext(LanguageLearningContext);
  if (!context) {
    throw new Error('useLanguageLearning must be used within LanguageLearningProvider');
  }
  return context;
};

function readSavedGuestLearningLanguage() {
  try {
    const saved = localStorage.getItem(LEARNING_LANGUAGE_STORAGE_KEY);
    return saved ? normalizeLearningLanguageCode(saved, DEFAULT_LEARNING_LANGUAGE_CODE) : null;
  } catch {
    return null;
  }
}

export const LanguageLearningProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [activeLanguage, setActiveLanguageState] = useState(DEFAULT_LEARNING_LANGUAGE_CODE);
  const [userLanguages, setUserLanguages] = useState([DEFAULT_LEARNING_LANGUAGE_CODE]);
  const [loading, setLoading] = useState(true);
  // The learning language the visitor chose: signed-in profile or saved guest choice.
  // null means no preference. Unlike activeLanguage, routes never change it.
  const [preferredLanguage, setPreferredLanguage] = useState(null);
  // Which account ('guest' or a uid) the language state above has been loaded for.
  const [loadedUserKey, setLoadedUserKey] = useState(null);
  const currentUserKey = user?.uid || 'guest';
  const currentUserKeyRef = useRef(currentUserKey);
  currentUserKeyRef.current = currentUserKey;
  // Ready only when auth has resolved and the state belongs to the current account,
  // so nothing renders a default language before a signed-in profile arrives.
  const ready = !authLoading && loadedUserKey === currentUserKey;

  const availableLanguages = AVAILABLE_LEARNING_LANGUAGES;

  useEffect(() => {
    if (!user) {
      const savedLanguages = readGuestLearningLanguages();
      const savedLanguage = normalizeLearningLanguageCode(
        localStorage.getItem(LEARNING_LANGUAGE_STORAGE_KEY) || savedLanguages[0],
        savedLanguages[0]
      );

      const resolvedActiveLanguage = savedLanguages.includes(savedLanguage)
        ? savedLanguage
        : savedLanguages[0];

      setUserLanguages(savedLanguages);
      setActiveLanguageState(resolvedActiveLanguage);
      setPreferredLanguage(readSavedGuestLearningLanguage());
      setLoading(false);
      setLoadedUserKey('guest');
      return;
    }

    const requestUserKey = user.uid;
    setLoadedUserKey(null);

    // Apply a profile response only if it still belongs to the signed-in account.
    const isCurrentRequest = () => currentUserKeyRef.current === requestUserKey;

    const fetchUserLanguages = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!isCurrentRequest()) return;

        if (userDoc.exists()) {
          const userData = userDoc.data() || {};
          const activeLangs = Array.isArray(userData.activeLearningLanguages)
            ? userData.activeLearningLanguages
              .map((code) => normalizeLearningLanguageCode(code, DEFAULT_LEARNING_LANGUAGE_CODE))
            : [];

          const profileLanguage = normalizeLearningLanguageCode(
            userData.learningLanguage || userData.preferredLearningLanguage || activeLangs[0],
            DEFAULT_LEARNING_LANGUAGE_CODE
          );
          const normalizedActiveLanguages = mergeLearningLanguages(activeLangs, profileLanguage);

          const resolvedLanguage = normalizedActiveLanguages.includes(profileLanguage)
            ? profileLanguage
            : normalizedActiveLanguages[0];
          setUserLanguages(normalizedActiveLanguages);
          setActiveLanguageState(resolvedLanguage);
          setPreferredLanguage(resolvedLanguage);
        } else {
          await setDoc(doc(db, 'users', user.uid), {
            activeLearningLanguages: [DEFAULT_LEARNING_LANGUAGE_CODE],
            preferredLearningLanguage: DEFAULT_LEARNING_LANGUAGE_CODE,
            learningLanguage: DEFAULT_LEARNING_LANGUAGE_CODE,
          }, { merge: true });
          if (!isCurrentRequest()) return;

          setUserLanguages([DEFAULT_LEARNING_LANGUAGE_CODE]);
          setActiveLanguageState(DEFAULT_LEARNING_LANGUAGE_CODE);
          setPreferredLanguage(DEFAULT_LEARNING_LANGUAGE_CODE);
        }
      } catch (error) {
        if (!isCurrentRequest()) return;
        console.error('Error fetching user languages:', error);
        setUserLanguages([DEFAULT_LEARNING_LANGUAGE_CODE]);
        setActiveLanguageState(DEFAULT_LEARNING_LANGUAGE_CODE);
        setPreferredLanguage(null);
      } finally {
        if (isCurrentRequest()) {
          setLoading(false);
          setLoadedUserKey(requestUserKey);
        }
      }
    };

    fetchUserLanguages();
  }, [user]);

  useEffect(() => {
    if (!ready || !activeLanguage || !isSelectableUiLanguageCode(activeLanguage)) {
      return;
    }
    if (!shouldUiLanguageFollowLearningLanguage()) {
      return;
    }
    if (typeof window !== 'undefined' && getPathBoundUiLanguage(window.location.pathname)) {
      return;
    }
    // A visitor who never chose a learning language is on the default one.
    // On marketing URLs the URL's language wins over that default.
    if (
      typeof window !== 'undefined'
      && preferredLanguage === null
      && getUrlDefaultUiLanguage(window.location.pathname)
    ) {
      return;
    }

    const nextUiLanguage = normalizeUiLanguageCode(activeLanguage);
    const currentUiLanguage = normalizeUiLanguageCode(i18n.resolvedLanguage || i18n.language);

    if (currentUiLanguage !== nextUiLanguage) {
      void i18n.changeLanguage(nextUiLanguage);
    }
  }, [activeLanguage, preferredLanguage, ready]);

  const service = useMemo(() => {
    if (!activeLanguage) return null;
    return new LanguageService(activeLanguage);
  }, [activeLanguage]);

  const setActiveLanguage = (languageCode) => {
    const normalizedCode = normalizeLearningLanguageCode(languageCode, DEFAULT_LEARNING_LANGUAGE_CODE);
    setActiveLanguageState(normalizedCode);
    return normalizedCode;
  };

  const switchLanguage = async (languageCode) => {
    const normalizedCode = normalizeLearningLanguageCode(languageCode, DEFAULT_LEARNING_LANGUAGE_CODE);

    if (!availableLanguages.find((language) => language.code === normalizedCode)) {
      console.error(`Language ${normalizedCode} is not available`);
      return;
    }

    setActiveLanguageState(normalizedCode);
    setPreferredLanguage(normalizedCode);

    if (user) {
      const newLanguages = mergeLearningLanguages(userLanguages, normalizedCode);
      if (newLanguages.length !== userLanguages.length) {
        setUserLanguages(newLanguages);
      }

      try {
        await updateDoc(doc(db, 'users', user.uid), {
          preferredLearningLanguage: normalizedCode,
          learningLanguage: normalizedCode,
          activeLearningLanguages: newLanguages,
        });
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
      return;
    }

    localStorage.setItem(LEARNING_LANGUAGE_STORAGE_KEY, normalizedCode);

    const currentLanguages = readGuestLearningLanguages();
    if (!currentLanguages.includes(normalizedCode)) {
      const newLanguages = [...currentLanguages, normalizedCode];
      persistGuestLearningLanguages(newLanguages);
      setUserLanguages(newLanguages);
    }
  };

  const addLearningLanguage = async (languageCode) => {
    const normalizedCode = normalizeLearningLanguageCode(languageCode, DEFAULT_LEARNING_LANGUAGE_CODE);

    if (!availableLanguages.find((language) => language.code === normalizedCode)) {
      console.error(`Language ${normalizedCode} is not available`);
      return;
    }

    if (userLanguages.includes(normalizedCode)) {
      return;
    }

    const newLanguages = [...userLanguages, normalizedCode];
    setUserLanguages(newLanguages);

    if (user) {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          activeLearningLanguages: newLanguages,
        });

        const newLanguageService = new LanguageService(normalizedCode);
        await newLanguageService.getUserProfile(user.uid);
      } catch (error) {
        console.error('Error adding language:', error);
      }
      return;
    }

    persistGuestLearningLanguages(newLanguages);
  };

  const removeLearningLanguage = async (languageCode) => {
    if (!user) {
      console.error('User must be logged in to remove languages');
      return;
    }

    const normalizedCode = normalizeLearningLanguageCode(languageCode, DEFAULT_LEARNING_LANGUAGE_CODE);

    if (userLanguages.length === 1) {
      console.error('Cannot remove the last language');
      return;
    }

    const newLanguages = userLanguages.filter((code) => code !== normalizedCode);
    setUserLanguages(newLanguages);

    if (activeLanguage === normalizedCode) {
      setActiveLanguageState(newLanguages[0]);
    }
    if (preferredLanguage === normalizedCode) {
      setPreferredLanguage(newLanguages[0]);
    }

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        activeLearningLanguages: newLanguages,
        ...(activeLanguage === normalizedCode && {
          preferredLearningLanguage: newLanguages[0],
          learningLanguage: newLanguages[0],
        }),
      });
    } catch (error) {
      console.error('Error removing language:', error);
    }
  };

  const getLanguageInfo = (languageCode) => {
    const normalizedCode = normalizeLearningLanguageCode(languageCode, DEFAULT_LEARNING_LANGUAGE_CODE);
    return availableLanguages.find((language) => language.code === normalizedCode)
      || getLearningLanguage(normalizedCode);
  };

  const hasLanguageAccess = (languageCode) => {
    const normalizedCode = normalizeLearningLanguageCode(languageCode, DEFAULT_LEARNING_LANGUAGE_CODE);
    return userLanguages.includes(normalizedCode);
  };

  const value = {
    activeLanguage,
    availableLanguages,
    userLanguages,
    loading,
    ready,
    preferredLanguage,
    hasLearningPreference: preferredLanguage !== null,
    service,
    switchLanguage,
    setActiveLanguage,
    addLearningLanguage,
    removeLearningLanguage,
    getLanguageInfo,
    hasLanguageAccess,
  };

  return (
    <LanguageLearningContext.Provider value={value}>
      {children}
    </LanguageLearningContext.Provider>
  );
};
